import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Trash2, FileText, BookmarkPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import JourneyStepper from '../components/JourneyStepper';
import {
  clearActiveDraftOrderId,
  notifyActiveDraftChanged,
  setActiveDraftOrderId,
} from '../lib/activeDraftOrder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderSummaryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [orderId, setOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (location.state?.orderId) {
      setOrderId(location.state.orderId);
      fetchOrder(location.state.orderId);
    } else {
      navigate('/quote');
    }
  }, [isAuthenticated, location, navigate]);

  const fetchOrder = async (id) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/enhanced-orders/${id}`,

      );
      setOrder(response.data);
      const status = (response.data?.status || '').toLowerCase();
      if (status === 'draft') {
        setActiveDraftOrderId(response.data.id);
      } else if (status) {
        clearActiveDraftOrderId();
      }
    } catch (error) {
      alert('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm('Remove this item from your order?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/enhanced-orders/${orderId}/remove-item/${itemId}`,

      );
      fetchOrder(orderId);
      // Refresh the TopNav cart badge — same draft id, fewer items.
      notifyActiveDraftChanged();
    } catch (error) {
      alert('Failed to remove item');
    }
  };

  const handleContinue = () => {
    navigate('/customer-info', {
      state: {
        orderId,
        shippingAddress: location.state?.shippingAddress,
        country: location.state?.country
      }
    });
  };

  const saveAsTemplate = async () => {
    const name = window.prompt(
      'Save this cart as a template?\n\nGive it a short name (e.g. "Monthly sticker reorder"):',
      order?.order_number || ''
    );
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API_URL}/api/templates/create-from-order`, {
        order_id: orderId,
        name: name.trim(),
      });
      alert(`Template "${name.trim()}" saved. Spin it up any time from your Dashboard.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const formatCurrency = (amount) => {
    const currency = (order?.currency || 'CAD').toUpperCase();
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Order not found</div>
      </div>
    );
  }

  const itemCount = order.items?.length || 0;
  const subtotal = order.total_price || 0;
  // Prefer the saved Freightcom quote if one is attached to the order
  // (user has already gone through /customer-info). Otherwise fall back to
  // a generic placeholder until they pick a rate.
  const savedShipping = Number(order.freightcom_quote?.rate) || null;
  const shipping = savedShipping ?? 4.99;
  const shippingCarrier = order.freightcom_quote?.carrier || null;
  const tax = 0; // Tax calculated later
  const total = subtotal + shipping + tax;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* iter114s.45 — Per-page brand band removed. */}
      <JourneyStepper currentStage={3} orderId={orderId} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Order Summary</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Order Items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                {itemCount} Item{itemCount !== 1 ? 's' : ''} in order
              </h2>

              {order.items && order.items.length > 0 ? (
                <div className="space-y-4" data-testid="order-items-list">
                  {order.items.map((item, index) => {
                    const artworkCount = (item.artwork_file_ids || []).length;
                    const hasArtwork = artworkCount > 0;
                    return (
                    <div
                      key={item.id || index}
                      className="border-b pb-4 last:border-b-0"
                      data-testid={`order-item-${item.id || index}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">
                            <span className="text-gray-600">Product:</span> {(() => {
                              // Prefer the friendly product_name over the
                              // raw product_type slug (iter114s.102).
                              const label = (item.product_name || item.product_type || '').trim();
                              return label.length >= 3 ? label : 'Custom sticker';
                            })()}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Size:</span>{' '}
                            {item.size
                              ? item.size
                              : `${item.width}x${item.height} ${item.unit || 'in'}`}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Quantity:</span> {item.quantity || 1}
                          </p>
                          {item.format && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Format:</span> {item.format}
                            </p>
                          )}
                          {item.cutline_shape && (
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Cutline:</span> {item.cutline_shape}
                            </p>
                          )}
                          <p
                            className={`text-sm mt-2 flex items-center gap-2 ${
                              hasArtwork ? 'text-[#4A7C59]' : 'text-amber-600'
                            }`}
                            data-testid={`item-artwork-status-${item.id || index}`}
                          >
                            <FileText className="w-4 h-4" />
                            {hasArtwork
                              ? `${artworkCount} artwork file${artworkCount > 1 ? 's' : ''} attached`
                              : 'No artwork yet'}
                            {!hasArtwork && (
                              <button
                                onClick={() =>
                                  navigate('/upload-artwork', {
                                    state: {
                                      orderId,
                                      itemId: item.id,
                                      addToExisting: true,
                                      shippingAddress: location.state?.shippingAddress,
                                      country: location.state?.country,
                                    },
                                  })
                                }
                                className="ml-2 px-3 py-1 text-xs font-semibold text-white bg-[#4A7C59] hover:bg-[#3d6849] rounded"
                                data-testid={`item-upload-artwork-btn-${item.id || index}`}
                              >
                                Upload now
                              </button>
                            )}
                          </p>
                          {item.instructions && (
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Instructions:</span> {item.instructions}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-bold text-lg text-gray-800">
                            {formatCurrency(item.price || 0)}
                          </p>
                          <button
                            onClick={() => removeItem(item.id)}
                            data-testid={`remove-item-btn-${item.id || index}`}
                            className="mt-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            REMOVE
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No items in order</p>
              )}

              <Button
                onClick={() => navigate('/quote', { state: { orderId, addToExisting: true } })}
                data-testid="order-summary-add-item-btn"
                className="w-full mt-6 bg-[#4A7C59] hover:bg-[#3d6849] text-white"
              >
                + ADD NEW ITEM TO MY ORDER
              </Button>
              {itemCount > 0 && (
                <Button
                  onClick={saveAsTemplate}
                  variant="outline"
                  data-testid="order-summary-save-template-btn"
                  className="w-full mt-3 border-[#4A7C59] text-[#4A7C59] hover:bg-green-50"
                >
                  <BookmarkPlus className="w-4 h-4 mr-2" />
                  Save this cart as a template
                </Button>
              )}
            </div>
          </div>

          {/* Right: Cost Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-100 rounded-lg shadow-lg p-6 sticky top-4">
              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-gray-700">
                  <span>Total Items</span>
                  <span className="font-semibold">{itemCount}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Total Cost</span>
                  <span className="font-semibold">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Shipping &amp; Handling*</span>
                  <span className="font-semibold" data-testid="shipping-cost">
                    {formatCurrency(shipping)}
                  </span>
                </div>
                {shippingCarrier && (
                  <p className="text-xs text-gray-500 -mt-2 italic">via {shippingCarrier}</p>
                )}
                <div className="flex justify-between text-gray-700">
                  <span>Tax</span>
                  <span className="font-semibold">--</span>
                </div>
                <div className="border-t-2 border-gray-300 pt-3">
                  <div className="flex justify-between text-xl font-bold text-gray-900">
                    <span>Total ({(order?.currency || 'CAD').toUpperCase()})</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleContinue}
                disabled={itemCount === 0}
                data-testid="order-summary-continue-btn"
                className="w-full bg-[#4A7C59] hover:bg-[#3d6849] text-white py-4 text-lg"
              >
                CONTINUE
              </Button>

              <p className="text-xs text-gray-500 mt-3 italic">
                * estimated shipping costs are subject to change upon order confirmation
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummaryPage;
